export interface IRouteDefinition {
    path: string
    segment: string
    name: string

    match(url: string): boolean
}

class RouteDefinition implements IRouteDefinition {
    constructor(
        public readonly name: string,
        public readonly segment: string,
        public readonly path: string,
        public readonly pathRegex?: RegExp,
        public readonly isAuthenticatedRoot: boolean = false,
        public readonly back?: string
    ) {}

    public match(url: string): boolean {
        return this.pathRegex?.test(url) ?? false
    }
}

export class RouteUrl {
    static getAllRoutes() {
        return Object.values(RouteUrl).filter(
            (route) => route instanceof RouteDefinition
        )
    }

    static getAuthenticatedRoutes() {
        return RouteUrl.getAllRoutes().filter(
            (route) => route.isAuthenticatedRoot
        )
    }

    static back(
        pathname: string,
        consultId: string | undefined,
        patientId: string | undefined
    ): string | undefined {
        const route = RouteUrl.getAllRoutes().find(
            (route) => !route.isAuthenticatedRoot && route.match(pathname)
        )
        if (!route) {
            // Same outcome as before: reading `.back` of no match threw.
            throw new TypeError(`No route matches '${pathname}'`)
        }

        const back = route.back
        if (back) {
            // String() keeps the previous runtime result for a missing param.
            return back
                .replace(':consultId', String(consultId))
                .replace(':patientId', String(patientId))
        }
        return undefined
    }

    static readonly Root = new RouteDefinition('Root', '/', '/')
    static readonly Home = new RouteDefinition('Home', '', '', /^\/$/)
    static readonly LogIn = new RouteDefinition(
        'Log in',
        'login',
        '/login',
        /login/
    )
    static readonly SignUp = new RouteDefinition(
        'Sign up',
        'signup',
        '/signup',
        /signup/
    )
    static readonly Authenticated = new RouteDefinition(
        'Authenticated',
        'authenticated',
        `/authenticated`,
        /^\/authenticated/,
        true
    )
    static readonly NewConsult = new RouteDefinition(
        'New consult',
        'consult',
        `/consult`,
        /^\/consult/,
        true
    )
    static readonly NewConsultTranscription = new RouteDefinition(
        'Transcription',
        'transcription',
        `${RouteUrl.NewConsult.path}/transcription`,
        /consult\/transcription\/?$/,
        false,
        RouteUrl.NewConsult.path
    )
    static readonly History = new RouteDefinition(
        'History',
        'history',
        `/history`,
        /history/,
        true
    )
    static readonly HistoryConsultDetailsById = new RouteDefinition(
        'History Consultation details by id',
        'consult/:consultId',
        `${RouteUrl.History.path}/consult/:consultId`,
        /history\/consult\/[a-zA-Z0-9-]+\/?$/,
        false,
        RouteUrl.History.path
    )
    // ACA-176: consults colleagues have shared with you (read-only).
    static readonly HistoryShared = new RouteDefinition(
        'Shared with me',
        'shared',
        `${RouteUrl.History.path}/shared`,
        /history\/shared\/?$/,
        false,
        RouteUrl.History.path
    )
    static readonly HistorySharedConsultById = new RouteDefinition(
        'Shared consult',
        'shared/:consultId',
        `${RouteUrl.HistoryShared.path}/:consultId`,
        /history\/shared\/[a-zA-Z0-9-]+\/?$/,
        false,
        RouteUrl.HistoryShared.path
    )
    static readonly Patient = new RouteDefinition(
        'Patient search',
        'patient',
        `/patient`,
        /patient/,
        true
    )
    static readonly PatientById = new RouteDefinition(
        'Patient details',
        ':patientId',
        `${RouteUrl.Patient.path}/:patientId`,
        /patient\/[a-zA-Z0-9]+\/?$/,
        false,
        RouteUrl.Patient.path
    )
    static readonly PatientTranscriptionById = new RouteDefinition(
        'Patient transcription',
        ':patientId/transcription',
        `${RouteUrl.PatientById.path}/transcription`,
        /patient\/[a-zA-Z0-9]+\/transcription\/?$/,
        false,
        RouteUrl.PatientById.path
    )
    static readonly PatientConsultDetailsById = new RouteDefinition(
        'Consult details by id',
        ':patientId/consult/:consultId',
        `${RouteUrl.PatientById.path}/consult/:consultId`,
        /patient\/[a-zA-Z0-9]+\/consult\/[a-zA-Z0-9-]+\/?$/
    )
    static readonly Settings = new RouteDefinition(
        'Settings',
        'settings',
        `/settings`,
        /settings/,
        true
    )
    static readonly Profile = new RouteDefinition(
        'Profile',
        'profile',
        `${RouteUrl.Settings.path}/profile`,
        /settings\/profile\/?$/
    )
    static readonly PlanManagement = new RouteDefinition(
        'Plan management',
        'plan-management',
        `${RouteUrl.Settings.path}/plan-management`,
        /settings\/plan-management\/?$/
    )
    static readonly PlanPricing = new RouteDefinition(
        'Plan Pricing',
        'pricing',
        `${RouteUrl.Settings.path}/pricing`,
        /settings\/pricing\/?$/
    )
    static readonly CheckoutSuccess = new RouteDefinition(
        'Checkout',
        'checkout/:sessionId',
        `checkout/:sessionId`,
        /^\/checkout\/[a-zA-Z0-9_-]+/,
        true
    )
    static readonly Language = new RouteDefinition(
        'Language',
        'language',
        `${RouteUrl.Settings.path}/language`,
        /settings\/language\/?$/
    )
    static readonly ReportIssue = new RouteDefinition(
        'Report an issue',
        'report',
        `${RouteUrl.Settings.path}/report`,
        /settings\/report\/?$/
    )
    static readonly SettingsTermsAndConditions = new RouteDefinition(
        'Terms and conditions',
        'terms-and-conditions',
        `${RouteUrl.Settings.path}/terms-and-conditions`,
        /settings\/terms-and-conditions/
    )
    static readonly Privacy = new RouteDefinition(
        'Privacy policy',
        'privacy',
        `${RouteUrl.Settings.path}/privacy`,
        /settings\/privacy\/?$/
    )
    static readonly Legal = new RouteDefinition(
        'Legal',
        'legal',
        `${RouteUrl.Settings.path}/legal`,
        /settings\/legal\/?$/
    )
    static readonly Forms = new RouteDefinition(
        'Forms',
        'forms',
        `/forms`,
        /forms/,
        true
    )
    static readonly FormReviewById = new RouteDefinition(
        'Form review',
        'review/:formId',
        `${RouteUrl.Forms.path}/review/:formId`,
        /forms\/review\/[a-zA-Z0-9-]+\/?$/,
        false
        // No hardcoded back - will use navigation state instead
    )
    static readonly FormEditById = new RouteDefinition(
        'Form edit',
        'edit/:formId',
        `${RouteUrl.Forms.path}/edit/:formId`,
        /forms\/edit\/[a-zA-Z0-9-]+\/?$/,
        false,
        RouteUrl.Forms.path
    )
    static readonly Admin = new RouteDefinition(
        'Admin',
        'admin',
        `/admin`,
        /admin/,
        true
    )
    static readonly AdminTemplates = new RouteDefinition(
        'Template management',
        'templates',
        `${RouteUrl.Admin.path}/templates`,
        /admin\/templates\/?$/,
        false,
        RouteUrl.Admin.path
    )
    static readonly AdminForms = new RouteDefinition(
        'Form management',
        'forms',
        `${RouteUrl.Admin.path}/forms`,
        /admin\/forms\/?$/,
        false,
        RouteUrl.Admin.path
    )
    static readonly AdminUsers = new RouteDefinition(
        'User management',
        'users',
        `${RouteUrl.Admin.path}/users`,
        /admin\/users\/?$/,
        false,
        RouteUrl.Admin.path
    )
    static readonly AdminLogs = new RouteDefinition(
        'Logs',
        'logs',
        `${RouteUrl.Admin.path}/logs`,
        /admin\/logs\/?$/,
        false,
        RouteUrl.Admin.path
    )
    static readonly Unauthorized = new RouteDefinition(
        'Unauthorized',
        'unauthorized',
        '/unauthorized',
        /unauthorized/
    )
    static readonly PrivacyStatement = new RouteDefinition(
        'Privacy',
        'privacy',
        '/privacy',
        /privacy/
    )
    static readonly TermsConditions = new RouteDefinition(
        'Terms and conditions',
        'terms-and-conditions',
        '/terms-and-conditions',
        /terms-and-conditions/
    )
}
